<script setup lang="ts">
  import { defineProps, computed, watch, onMounted, ref } from 'vue'
  import * as yup from 'yup'
  import { PlusIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/vue/24/outline'
  import {
    type SuperUserQuestionnaire as Questionnaire,
    type QuestionnaireSection,
    type QuestionnaireQuestion,
    FieldTypeEnum,
    type GenericDataMapper,
    TagRelationshipEnum
  } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import { type Option } from '@/components/admin/form/SelectField.vue'

  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const questionnaire = computed<Questionnaire | null>(() => questionnaireEditorStore.questionnaire)

  const tags = computed<GenericDataMapper[]>(() => questionnaireEditorStore.tags)
  const tagOptions = computed<Option[]>(() => questionnaireEditorStore.tagOptions)
  const relationshipOptions = computed<Option[]>(() => questionnaireEditorStore.relationshipOptions)
  const fieldTypesOptions = computed<Option[]>(() => questionnaireEditorStore.fieldTypesOptions)
  const availableFieldTypesOptions = computed<Option[]>(() => {
    if (selectedTag.value === null) {
      return fieldTypesOptions.value
    }
    return fieldTypesOptions.value.filter((option) => {
      return selectedTag.value?.field_type === option.value
    })
  })
  const selectedTag = computed<GenericDataMapper | null>(() => {
    return tags.value.find((tag) => tag.key === question.value?.tag) ?? null
  })

  // Track the previous tag value to detect actual tag changes vs question switches
  const previousTagKey = ref<string | null | undefined>(undefined)

  const props = defineProps({
    uuid: {
      type: String,
      required: true
    },
    sectionUuid: {
      type: String,
      required: true
    }
  })
  const section = computed<QuestionnaireSection | undefined>(() =>
    questionnaire.value?.schema.find((p) => p.uuid === props.sectionUuid)
  )
  const question = computed<QuestionnaireQuestion | undefined>(() =>
    section.value?.items.find((q) => q.uuid === props.uuid)
  )
  const showOptions = computed(
    () =>
      question.value?.type === FieldTypeEnum.SELECT ||
      question.value?.type === FieldTypeEnum.MULTISELECT ||
      question.value?.type === FieldTypeEnum.YESNO
  )

  const showLogicJump = computed(() => {
    return (
      question.value?.type === FieldTypeEnum.SELECT || question.value?.type === FieldTypeEnum.YESNO
    )
  })

  const updateOption = (newValue: string, index: number) => {
    question.value!.choices[index] = newValue
    if (question.value!.jump_to !== null) {
      question.value!.jump_to[index].value = newValue
    }
  }
  const updateJumpTo = (newValue: string, index: number) => {
    if (question.value && question.value.jump_to && question.value.jump_to[index]) {
      question.value.jump_to[index].uuid = newValue
    }
  }

  const isJumpToEnabled = computed(() => {
    return question.value?.jump_to !== null
  })

  const toggleJumpTo = () => {
    if (!isJumpToEnabled.value) {
      const newJumpTo = question.value!.choices.map((choice) => ({
        value: choice,
        uuid: null
      }))
      question.value!.jump_to = newJumpTo
    } else {
      question.value!.jump_to = null
    }
  }
  const addOption = () => {
    question.value!.choices.push('')
    if (question.value!.jump_to !== null) {
      question.value!.jump_to.push({ value: '', uuid: null })
    }
  }
  const removeOption = (index: number) => {
    question.value!.choices.splice(index, 1)
    if (question.value!.jump_to !== null) {
      question.value!.jump_to.splice(index, 1)
    }
  }

  // Auto-create Yes & No options for yesno questions with empty choices
  const initYesNoChoices = () => {
    if (!question.value) return
    // Initialize choices array if it doesn't exist
    if (!question.value.choices) {
      question.value.choices = []
    }

    if (question.value.type === FieldTypeEnum.YESNO) {
      if (!question.value.choices.length) {
        question.value.choices = ['Yes', 'No']
      }
      // Get existing jump_to if it exists, otherwise create new
      const existingJumpTo = question.value.jump_to
      if (existingJumpTo) {
        const newJumpTo = [
          { value: 'Yes', uuid: existingJumpTo[0]?.uuid ?? null },
          { value: 'No', uuid: existingJumpTo[1]?.uuid ?? null }
        ]
        question.value.jump_to = newJumpTo
      }
    }
  }

  // Initialize when type changes
  watch(
    () => question.value?.type,
    (newType, oldType) => {
      // Only initialize if changing to yesno type
      if (
        newType === FieldTypeEnum.YESNO ||
        (newType === FieldTypeEnum.SELECT && newType !== oldType)
      ) {
        initYesNoChoices()
      }
    }
  )

  // Initialize on mount
  onMounted(() => {
    // Small delay to ensure question is loaded
    setTimeout(() => {
      initYesNoChoices()
    }, 0)
  })

  // Watch for question changes to reset state tracking
  watch(
    () => props.uuid,
    (newUuid) => {
      // Reset the previous tag key when switching questions
      // This prevents the tag watcher from triggering on question switch
      previousTagKey.value = undefined
    },
    { immediate: true }
  )

  // Watch for actual tag changes (when user changes the connected field)
  // This should NOT trigger when switching between questions
  watch(
    () => question.value?.tag as string | null | undefined,
    (newTagKey) => {
      if (!question.value) {
        return
      }

      // Normalize the tag key value
      const normalizedNewTagKey = newTagKey || null

      // Skip if previousTagKey is undefined (initial load or just switched questions)
      if (previousTagKey.value === undefined) {
        // Initialize the previous tag key for future comparisons
        previousTagKey.value = normalizedNewTagKey
        return
      }

      // Check if the tag actually changed
      const tagChanged = normalizedNewTagKey !== previousTagKey.value

      // Update the previous tag key
      previousTagKey.value = normalizedNewTagKey

      // Only proceed if the tag actually changed (user changed the connected field)
      if (!tagChanged) {
        return
      }

      // Now handle the actual tag change
      if (newTagKey) {
        const tag = tags.value.find((t) => t.key === newTagKey)
        if (tag) {
          question.value!.type = tag.field_type

          // Handle choices and jump_to based on field type
          if (tag.field_type !== FieldTypeEnum.YESNO || (tag.choices && tag.choices.length > 0)) {
            question.value!.choices = JSON.parse(JSON.stringify(tag.choices || []))
            question.value!.jump_to = null
          }

          question.value!.starts_ai_analysis = tag.starts_ai_analysis
        }
      } else {
        question.value!.starts_ai_analysis = false
      }
    }
  )

  const jumpToOptions = computed<Option[]>(() => {
    const seenCurrentQuestion = { found: false }

    // Mapping and filtering the questionnaire schema
    const options = (questionnaire.value?.schema ?? [])
      .flatMap((section) => {
        return section.items.map((q) => ({
          ...q,
          sectionTitle: section.title
        }))
      })
      .filter((q) => {
        // Skip until we've found the current question
        if (q.uuid === props.uuid) {
          seenCurrentQuestion.found = true
          return false // Do not include the current question itself
        }

        return seenCurrentQuestion.found
      })
      .map((q) => ({
        value: q.uuid,
        title: `${q.sectionTitle} | ${q.label}`
      })) as Option[]

    // Add an option at the beginning and another one at the end of the array
    return [
      { value: '', title: 'Select...' }, // Option at the beginning
      ...options, // Dynamically generated options
      { value: 'END', title: 'End of questionnaire' } // Option at the end
    ]
  })

  const isUniquePerCaseRelationship = computed(() => {
    const currentTag = question.value?.tag_relationship
    if (currentTag) {
      return [TagRelationshipEnum.ENTITIES_CLIENT, TagRelationshipEnum.ENTITIES_OPPONENT].includes(
        currentTag as TagRelationshipEnum
      )
    }
    return false
  })

  const isEntityTag = computed(() => {
    return selectedTag.value?.is_entity_field ?? false
  })

  const tagNumberOptions = computed<Option[]>(() => {
    return Array.from({ length: 10 }, (_, i) => ({
      value: i + 1,
      title: (i + 1).toString()
    }))
  })

  // Watch tag_number to ensure it's always between 1 and 10 when it has a value
  watch(
    () => question.value?.tag_number,
    (newTagNumber) => {
      if (question.value && newTagNumber !== undefined && newTagNumber !== null) {
        let numValue: number
        if (typeof newTagNumber === 'string') {
          numValue = parseInt(newTagNumber, 10)
        } else {
          numValue = newTagNumber
        }

        // Ensure the value is null if it's not between 1 and 10
        if (isNaN(numValue) || numValue < 1 || numValue > 10) {
          question.value.tag_number = null
        }
      }
    },
    { immediate: true } // Check immediately when component is mounted
  )

  const optional = computed({
    get: () => {
      const val = question.value?.optional ?? false
      return val
    },
    set: (value) => {
      if (question.value) {
        question.value.optional = value
      }
    }
  })
</script>
<template>
  <div
    v-if="section && question"
    class="mb-4 mt-4 rounded-xl border bg-white p-4 shadow-sm md:p-5"
    :class="question.starts_ai_analysis ? 'border-purple-700' : 'border-gray-200'"
  >
    <div class="mb-4 w-full md:w-1/2">
      <InputField
        v-model="question.label"
        name="label"
        label="Label"
      />
    </div>

    <div class="mb-4 w-full md:w-1/2">
      <InputField
        v-model="question.help"
        name="help"
        label="Help Text"
      />
    </div>
    <div class="mb-4 w-full md:w-1/2">
      <div class="flex items-center gap-2 pb-2 align-bottom">
        <!-- Wrapper div to control the width of SelectField -->
        <div class="w-full max-w-full flex-1 md:w-auto">
          <SelectField
            v-model="question.tag"
            :options="tagOptions"
            :is-filterable="true"
            name="tag"
            label="Tag"
          />
        </div>
        <div v-if="isEntityTag">
          <SelectField
            v-model="question.tag_relationship"
            :options="relationshipOptions"
            :is-filterable="true"
            name="tag_relationship"
            label="Relationship"
          />
        </div>
        <div v-if="isEntityTag && !isUniquePerCaseRelationship">
          <SelectField
            v-model="question.tag_number"
            :options="tagNumberOptions"
            :is-filterable="false"
            name="tag_number"
            label="Number"
          />
        </div>
        <div v-if="question.tag">
          <label class="mb-2 block text-sm font-medium">&nbsp;</label>
          <button
            type="button"
            class="inline-flex items-center gap-x-2 self-center rounded-lg border border-gray-200 bg-white px-3 py-2
              text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
              disabled:pointer-events-none disabled:opacity-50"
            @click="question.tag = ''"
          >
            <TrashIcon class="size-4 shrink-0" />
          </button>
        </div>
      </div>
      <div
        v-if="question.starts_ai_analysis"
        class="text-sm text-purple-700"
      >
        Starts AI analysis
      </div>
    </div>
    <div class="mb-4 w-full md:w-1/2">
      <SelectField
        v-model="question.type"
        :options="availableFieldTypesOptions"
        name="type"
        label="Field type"
        :disabled="selectedTag !== null"
      />
    </div>
    <div
      v-if="showOptions"
      class="mb-4 w-full md:w-1/2"
    >
      <div
        v-for="(option, index) in question.choices"
        :key="index"
        class="ms-2 flex items-center gap-2 pb-2"
      >
        <input
          :type="question.type === FieldTypeEnum.MULTISELECT ? 'checkbox' : 'radio'"
          disabled
          class="self-center"
        />
        <div class="flex min-w-0 grow items-center gap-2">
          <div :class="[isJumpToEnabled ? 'flex-1' : 'grow']">
            <InputField
              :name="`choice-${index}`"
              :modelValue="option"
              :disabled="selectedTag !== null"
              @update:model-value="(value) => updateOption(value, index)"
            />
          </div>
          <div
            v-if="isJumpToEnabled"
            class="w-1/2"
          >
            <SelectField
              :name="`jump-to-${index}`"
              :options="jumpToOptions"
              :modelValue="
                question.jump_to && question.jump_to[index] ? question.jump_to[index].uuid : ''
              "
              @update:model-value="(value) => updateJumpTo(value, index)"
            />
          </div>
        </div>
        <button
          v-if="selectedTag === null"
          type="button"
          class="inline-flex items-center gap-x-2 self-center rounded-lg border border-gray-200 bg-white px-3 py-2
            text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          @click="removeOption(index)"
        >
          <TrashIcon class="size-4 shrink-0" />
        </button>
      </div>

      <div class="flex items-center justify-between">
        <div v-if="selectedTag === null">
          <button
            type="button"
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-3 text-sm font-medium
              text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
              disabled:opacity-50 dark:text-white dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
            @click="addOption"
          >
            <PlusIcon class="size-4 shrink-0" />
            Add option
          </button>
        </div>
        <div
          v-if="showLogicJump"
          class="flex items-center"
        >
          <input
            id="jumpto"
            class="ms-2 h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            type="checkbox"
            name="jumpto"
            :checked="isJumpToEnabled"
            @change="toggleJumpTo"
          />
          <label
            class="ml-2 block text-sm font-medium text-gray-700"
            for="jumpto"
          >
            Logic jump
          </label>
        </div>
      </div>
    </div>
    <hr class="mb-3 mt-3" />
    <div class="mt-5 flex items-center justify-between">
      <div class="mt-1">
        <CheckboxField
          v-model="optional"
          name="optional"
          :checked-value="true"
          :unchecked-value="false"
          label="Optional"
          :rules="yup.boolean().oneOf([true, false], 'You must agree to the terms')"
        />
      </div>
      <div class="ms-auto flex">
        <button
          type="button"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
            font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          @click="questionnaireEditorStore.copyQuestion(section, question)"
        >
          <DocumentDuplicateIcon class="size-4 shrink-0" />
          Duplicate
        </button>
        <button
          type="button"
          class="ms-2 inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
            font-medium text-red-600 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
            disabled:pointer-events-none disabled:opacity-50"
          @click="questionnaireEditorStore.removeQuestion(section, question)"
        >
          <TrashIcon class="size-4 shrink-0" />
          Remove question
        </button>
      </div>
    </div>
  </div>
</template>
